// src/components/resources/tools/generators/AdMockup/GoogleSearchAd.tsx
'use client'

import { clsx } from 'clsx'
import type { AdState } from './types'

/**
 * Google Responsive Search Ad preview. Real RSAs render: a bold "Sponsored"
 * label, the business URL with breadcrumb display paths, a blue clickable
 * headline (Google rotates/joins up to 3 with " | "), and a grey description
 * line (up to 2 joined). The control panel enforces Google's real character
 * limits live (≤30 / ≤90 / ≤15) — this preview shows the result. Empty
 * headlines/descriptions are dropped so the preview matches what Google would
 * actually assemble.
 */
export function GoogleSearchAd({ state }: { state: AdState }): JSX.Element {
  const dark = state.theme === 'dark'
  const headlines = state.gHeadlines.map(h => h.trim()).filter(Boolean)
  const descriptions = state.gDescriptions.map(d => d.trim()).filter(Boolean)
  const paths = [state.gPath1, state.gPath2].map(p => p.trim()).filter(Boolean)
  // Google RSA display URLs show the final URL's HOST plus the two explicit
  // path fields — so if a user pastes a full final URL, strip the
  // protocol/path/query/fragment down to the hostname before appending the
  // breadcrumb paths (otherwise the preview duplicates paths, e.g.
  // `example.com/sale?utm=1 › path1 › path2`).
  const domain =
    state.displayUrl
      .trim()
      .replace(/^https?:\/\//i, '')
      .replace(/[/?#].*$/s, '') || 'yourbrand.com'
  return (
    <div className={clsx('rounded-xl border p-4 font-sans shadow-md', dark ? 'border-slate-700 bg-[#202124] text-slate-100' : 'border-slate-200 bg-white text-slate-900')}>
      <div className="flex items-center gap-1.5 text-xs">
        <span className="font-semibold">Sponsored</span>
      </div>
      <div className={clsx('mt-1.5 flex items-center gap-1 text-sm', dark ? 'text-slate-200' : 'text-slate-800')}>
        <span className="truncate">
          {domain}
          {paths.length > 0 && <span className={dark ? 'text-slate-400' : 'text-slate-500'}> › {paths.join(' › ')}</span>}
        </span>
      </div>
      <h3 className={clsx('mt-0.5 text-xl leading-snug', dark ? 'text-[#8ab4f8]' : 'text-[#1a0dab]')}>
        {headlines.length > 0 ? headlines.join('  |  ') : 'Your headline | appears here'}
      </h3>
      <p className={clsx('mt-1 text-sm leading-snug', dark ? 'text-slate-300' : 'text-[#4d5156]')}>
        {descriptions.length > 0 ? descriptions.join(' ') : 'Your ad descriptions appear here, joined into a single line.'}
      </p>
    </div>
  )
}
